import { Check, Plus, Trash2 } from 'lucide-react';
import { useId, type CSSProperties, type ReactNode } from 'react';
import type { TemplateBox as TemplateBoxSpec } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Input, Textarea } from '../../../components/Input';
import { Select, type SelectOption } from '../../../components/Select';
import styles from './TemplateBox.module.css';

/**
 * ADR 0083: one box of a hiring template (the Ideal candidate profile, the 30 and 90-day plans, the Leadership add-on), laid out
 * as the template prints it: the number and question, its min and max, a note, the rows, the grey "e.g." and what it still needs.
 * The form offers at most `max` rows and always shows the `min` required ones, numbered with a *. On a tablet and a phone each
 * row becomes a small card with its column names, so nothing scrolls sideways.
 */

export type CellValue = string | number | boolean | null;

export interface TemplateColumn<R> {
  readonly key: keyof R & string;
  readonly label: string;
  /** text: one line; long: a few lines; day: "Day N"; number: a count; choice: one of `options`; yesno: Yes or No. */
  readonly kind: 'text' | 'long' | 'day' | 'number' | 'choice' | 'yesno';
  readonly options?: readonly SelectOption[];
  /** What the box's example says for this column, unless given. */
  readonly placeholder?: string;
  /** The column's share of the row on a wide screen (CSS grid track). */
  readonly track?: string;
}

const NUMBER_TRACK = '2.75rem';
const ACTION_TRACK = '2.25rem';
const YES_NO: readonly SelectOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

interface BoxProps {
  readonly box: TemplateBoxSpec;
  /** The rule shown beside the title, when it differs from the template's (90-day box 3 on a leadership role). */
  readonly rule?: string;
  /** What the box still needs (from the shared progress rules); null when done. */
  readonly problem: string | null;
  /** Whether the box counts towards complete at all: an optional, empty box shows no tick. */
  readonly isCounted: boolean;
  readonly children: ReactNode;
}

/** The frame: number, question, rule, note, then the rows, the example, and what is still needed or a tick. */
export function TemplateBoxFrame({ box, rule, problem, isCounted, children }: BoxProps): ReactNode {
  const titleId = useId();
  return (
    <section className={styles.box} aria-labelledby={titleId}>
      <header className={styles.head}>
        <h3 id={titleId} className={styles.title}>
          <span className={styles.number}>{box.number}.</span> {box.title}
        </h3>
        <span className={styles.rule}>{rule ?? box.rule}</span>
        {problem === null && isCounted && (
          <span className={styles.done}>
            <Check size={14} aria-hidden /> Done
          </span>
        )}
      </header>
      {box.note && <p className={styles.note}>{box.note}</p>}
      {children}
      {box.example !== '' && <p className={styles.example}>e.g. {box.example}</p>}
      {problem !== null && (
        <p className={styles.problem} role="status">
          Still needed: {problem.replace(/^Box [\d.]+: /, '')}
        </p>
      )}
    </section>
  );
}

function examplePlaceholders(box: TemplateBoxSpec): readonly string[] {
  return box.example.split(' · ').map((part) => part.replace(/^Day /, '').trim());
}

interface CellProps<R> {
  readonly column: TemplateColumn<R>;
  readonly value: CellValue;
  readonly placeholder: string | undefined;
  readonly ariaLabel: string;
  readonly disabled: boolean;
  readonly onChange: (value: CellValue) => void;
}

function numberOf(text: string, previous: CellValue, max: number): CellValue {
  if (text.trim() === '') return null;
  const parsed = Number.parseInt(text.replace(/\D/g, ''), 10);
  if (Number.isNaN(parsed)) return previous;
  return Math.min(parsed, max);
}

/** One control, ours (CLAUDE.md §5): an input, a few lines, a day number, a count, or a pick from a fixed list. */
function Cell<R>({ column, value, placeholder, ariaLabel, disabled, onChange }: CellProps<R>): ReactNode {
  switch (column.kind) {
    case 'long':
      return <Textarea aria-label={ariaLabel} rows={2} disabled={disabled} placeholder={placeholder} value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value)} />;
    case 'day':
      return (
        <Input
          aria-label={ariaLabel}
          prefix="Day"
          inputMode="numeric"
          isNumeric
          disabled={disabled}
          placeholder={placeholder}
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(event) => onChange(numberOf(event.target.value, value, 365))}
        />
      );
    case 'number':
      return (
        <Input
          aria-label={ariaLabel}
          inputMode="numeric"
          isNumeric
          disabled={disabled}
          placeholder={placeholder}
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(event) => onChange(numberOf(event.target.value, value, 999))}
        />
      );
    case 'choice':
    case 'yesno': {
      const options = column.kind === 'yesno' ? YES_NO : (column.options ?? []);
      const current = column.kind === 'yesno' ? (value === true ? 'yes' : value === false ? 'no' : null) : typeof value === 'string' ? value : null;
      return (
        <Select
          label={ariaLabel}
          disabled={disabled}
          placeholder={placeholder ?? 'Choose'}
          value={current}
          options={current === null ? options : [{ value: '', label: 'Clear' }, ...options]}
          onChange={(next) => {
            if (next === '') onChange(null);
            else onChange(column.kind === 'yesno' ? next === 'yes' : next);
          }}
        />
      );
    }
    default:
      return <Input aria-label={ariaLabel} disabled={disabled} placeholder={placeholder} value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value)} />;
  }
}

function cellOf<R>(row: R, key: keyof R & string): CellValue {
  const value = (row as Record<string, unknown>)[key];
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? value : null;
}

function isEmptyRow<R>(row: R, columns: readonly TemplateColumn<R>[]): boolean {
  return columns.every((column) => {
    const value = cellOf(row, column.key);
    return value === null || (typeof value === 'string' && value.trim() === '');
  });
}

interface RowsProps<R> {
  readonly box: TemplateBoxSpec;
  readonly rows: readonly R[];
  readonly columns: readonly TemplateColumn<R>[];
  readonly emptyRow: () => R;
  readonly onChange: (rows: R[]) => void;
  readonly disabled?: boolean;
  /** The rows that must be filled; the template's own minimum unless the role changes it. */
  readonly min?: number;
}

/** A box of numbered rows: at least `min` (each marked *), at most `max`. Rows past the minimum can be removed. */
export function TemplateRows<R>({ box, rows, columns, emptyRow, onChange, disabled = false, min = box.min }: RowsProps<R>): ReactNode {
  const shown = Math.min(box.max, Math.max(min, 1, rows.length));
  const padded = Array.from({ length: shown }, (_, index) => rows[index] ?? emptyRow());
  const examples = examplePlaceholders(box);
  const style = { '--tpl-cols': `${NUMBER_TRACK} ${columns.map((column) => column.track ?? 'minmax(0, 1fr)').join(' ')} ${ACTION_TRACK}` } as CSSProperties;

  const change = (index: number, key: keyof R & string, value: CellValue): void => {
    onChange(padded.map((row, at) => (at === index ? ({ ...row, [key]: value } as R) : row)));
  };

  return (
    <div className={styles.rows} style={style}>
      <div className={styles.headRow} aria-hidden>
        <span>#</span>
        {columns.map((column) => (
          <span key={column.key}>{column.label}</span>
        ))}
        <span />
      </div>
      {padded.map((row, index) => {
        const isRequired = index < min;
        const canRemove = !disabled && !isRequired && (index >= Math.max(min, 1) || !isEmptyRow(row, columns));
        return (
          <div key={index} className={styles.row}>
            <span className={styles.rowNumber}>
              {index + 1}
              {isRequired && <span className={styles.star} aria-label="required"> *</span>}
            </span>
            {columns.map((column, at) => (
              <div key={column.key} className={styles.cell}>
                <span className={styles.cellLabel} aria-hidden>
                  {column.label}
                </span>
                <Cell
                  column={column}
                  value={cellOf(row, column.key)}
                  placeholder={column.placeholder ?? examples[at]}
                  ariaLabel={`${column.label}, row ${index + 1}`}
                  disabled={disabled}
                  onChange={(value) => change(index, column.key, value)}
                />
              </div>
            ))}
            <span className={styles.rowAction}>
              {canRemove && <Button variant="ghost" size="sm" icon={Trash2} aria-label={`Remove row ${index + 1}`} onClick={() => onChange(padded.filter((_, at) => at !== index))} />}
            </span>
          </div>
        );
      })}
      {!disabled && padded.length < box.max && (
        <div className={styles.add}>
          <Button variant="ghost" size="sm" icon={Plus} onClick={() => onChange([...padded, emptyRow()])}>
            Add a row
          </Button>
        </div>
      )}
    </div>
  );
}

interface FieldsProps<R> {
  readonly box: TemplateBoxSpec;
  readonly value: R;
  readonly columns: readonly TemplateColumn<R>[];
  readonly onChange: (value: R) => void;
  readonly disabled?: boolean;
  /** Mark every field required (*). */
  readonly isRequired: boolean;
}

/** A box of fixed fields ("all required", "exactly 1"): each field labelled, side by side where there is room. */
export function TemplateFields<R>({ box, value, columns, onChange, disabled = false, isRequired }: FieldsProps<R>): ReactNode {
  const examples = examplePlaceholders(box);
  return (
    <div className={styles.fields}>
      {columns.map((column, at) => (
        <div key={column.key} className={column.kind === 'long' ? styles.wideField : styles.field}>
          <span className={styles.fieldLabel} aria-hidden>
            {column.label}
            {isRequired && <span className={styles.star}> *</span>}
          </span>
          <Cell
            column={column}
            value={cellOf(value, column.key)}
            placeholder={column.placeholder ?? examples[at]}
            ariaLabel={isRequired ? `${column.label} (required)` : column.label}
            disabled={disabled}
            onChange={(next) => onChange({ ...value, [column.key]: next } as R)}
          />
        </div>
      ))}
    </div>
  );
}

/** A heading for the Leadership add-on's part inside a template. */
export function AddOnHeading({ title, children }: { readonly title: string; readonly children: ReactNode }): ReactNode {
  return (
    <div className={styles.addOn}>
      <h3 className={styles.addOnTitle}>{title}</h3>
      <p className={styles.note}>{children}</p>
    </div>
  );
}
